import { Injectable } from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';

import { Observable, of, Subject } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { Cacheable, CacheBuster } from 'ts-cacheable';
import { buildQueryParams } from "projects/shared/src/lib/helpers/url";
import { SharedSettings } from '../models/shared-settings';

import { Customer, CustomerAssignmentSummary } from '../models/customer';
import { CustomerSite, DuplicateCustomerSiteGroup, FindDuplicateSitesRequest } from '../models/customer-site';

import { url } from '../helpers/url';
import { DateTime } from 'luxon';

@Injectable({
    providedIn: 'root'
})
export class CustomerService {
    private static readonly cacheObservable = new Subject<void>();

    constructor(private http: HttpClient, private settings: SharedSettings) {
    }

    @Cacheable({
        cacheBusterObserver: CustomerService.cacheObservable,
        maxAge: 5 * 60 * 1000
    })
    getAllCustomers(): Observable<Customer[]> {
        return this.findCustomers();
    }

    findCustomers(params?: { accountNumber?: string }): Observable<Customer[]> {
        const queryParams = params !== undefined ? buildQueryParams(params).toString() : '';

        return this.http.get<Customer[]>(this.settings.apiBaseUrl + `customers?${queryParams}`);
    }

    @Cacheable({
        cacheBusterObserver: CustomerService.cacheObservable,
        maxAge: 5 * 60 * 1000
    })
    getCustomers(siteId: string, date?: DateTime, loadType?: 'inbound' | 'outbound' | null): Observable<CustomerAssignmentSummary> {
        let params = new HttpParams();
        if (date) {
            params = params.append('date', date.toISODate());
        }
        if (loadType) {
            params = params.append('loadType', loadType);
        }
        return this.http.get<CustomerAssignmentSummary>(
            this.settings.apiBaseUrl + url`sites/${siteId}/customers`,
            {params: params} );
    }

    getCustomer(customerId: number): Observable<Customer> {
        return this.http.get<Customer>(this.settings.apiBaseUrl + url`customers/${customerId}`);
    }

    @CacheBuster({
        cacheBusterNotifier: CustomerService.cacheObservable
    })
    createCustomer(customer: Customer): Observable<Customer> {
        return this.http.post<Customer>(this.settings.apiBaseUrl + url`customers/`, customer);
    }

    @CacheBuster({
        cacheBusterNotifier: CustomerService.cacheObservable
    })
    updateCustomer(customer: Customer): Observable<Customer> {
        return this.http.put<Customer>(this.settings.apiBaseUrl + url`customers/`, customer);
    }

    @CacheBuster({
        cacheBusterNotifier: CustomerService.cacheObservable
    })
    createShipTo(site: CustomerSite, siteId: string = ''): Observable<CustomerSite> {
        return this.http.post<CustomerSite>(this.settings.apiBaseUrl + url`customers/${site.customerId}/sites?siteId=${siteId}`, site);
    }

    /**
     * Finds existing customer sites that are duplicates of the given site criteria.
     * @param customerId
     * @param request
     */
    findDuplicateSites(customerId: number, request: FindDuplicateSitesRequest): Observable<CustomerSite[]> {
        return this.http.post<CustomerSite[]>(this.settings.apiBaseUrl + url`customers/${customerId}/sites/duplicates`, request).pipe(
            catchError(() => of([]))
        );
    }

    /**
     * Returns ALL duplicate sites for a customer grouped by the site.
     * @param customerId
     */
    findAllDuplicateSites(customerId: number): Observable<DuplicateCustomerSiteGroup[]> {
        return this.http.get<DuplicateCustomerSiteGroup[]>(
            this.settings.apiBaseUrl + url`customers/${customerId}/duplicate-sites`
        );
    }

    @CacheBuster({
        cacheBusterNotifier: CustomerService.cacheObservable
    })
    updateShipTo(shipTo: CustomerSite): Observable<CustomerSite> {
        return this.http.put<CustomerSite>(
            this.settings.apiBaseUrl + url`customers/${shipTo.customerId}/sites/${shipTo.id}`, shipTo);
    }

    @CacheBuster({
        cacheBusterNotifier: CustomerService.cacheObservable
    })
    replaceShipTo(customerId: number, customerSiteId: number, replacementSiteId: number): Observable<void> {
        return this.http.delete<void>(
            this.settings.apiBaseUrl + url`customers/${customerId}/sites/${customerSiteId}?replaceWith=${replacementSiteId}`);
    }

    @CacheBuster({
        cacheBusterNotifier: CustomerService.cacheObservable
    })
    updateCustomerFromOracle(customerId: number): Observable<void> {
        return this.http.post<void>(this.settings.apiBaseUrl + url`customers/${customerId}/update`, null);
    }
}
